import { Camelized } from 'humps';
import { get, isEmpty } from 'lodash-es';
import { FORM_FACTORS_MAP } from '@namespace/device';
import {
  FeedContentType,
  IGetGamesFeedResponse
} from '../../hooks/api/getGamesFeed';
import { IGameDetails } from '../../hooks/api/getInnerGamesIds';
import {
  ColdHotListGamesDataType,
  IColdHotListGames
} from '../../hooks/api/suggesterGames';
import {
  GamesProvidersType,
  IGame,
  IGames,
  IGamesProvider,
  IGamesProviderNormalised,
  INormalizedGame,
  PlatformType
} from '../../types';
import { getGameGroup } from '../../utils';
import {
  IFeedCategoriesNormalized,
  IGamesStore,
  InnerGameIdToLaunchIdType,
  LaunchIdToGameLiveDataType
} from '../types';

const normalizeCategoriesFromCms = (
  categories: string[],
  content: FeedContentType,
  normalizedGames: IGamesStore
): IFeedCategoriesNormalized =>
  categories.reduce(
    (acc: IFeedCategoriesNormalized, categoryId) => {
      const item = content[categoryId];

      if (isEmpty(item) || item.games.length === 0) {
        return acc;
      }

      const filteredGames = item.games.filter(
        (gameId) => normalizedGames.data[gameId]
      );

      if (filteredGames.length === 0) {
        return acc;
      }

      const name = item.name.replace('translation.', '');

      return {
        ...acc,
        data: Object.assign(acc.data, {
          [name]: {
            id: name,
            title:
              item.settings?.nameTranslationKey ||
              `content.games.category.${name}`,
            ...item.settings,
            games: filteredGames,
            instantGames: item.instantGames,
            total:
              (filteredGames?.length ?? 0) + (item.instantGames?.length ?? 0)
          }
        }),
        ids: [...acc.ids, name],
        isLoaded: true
      };
    },
    { data: {}, ids: [], isLoaded: true }
  );

export const normalizeGameFromCms = (
  game: IGame,
  provider: IGamesProvider | undefined,
  platformArg: PlatformType
): INormalizedGame => {
  const platform: PlatformType =
    platformArg === FORM_FACTORS_MAP.DESKTOP
      ? platformArg
      : FORM_FACTORS_MAP.MOBILE;

  const {
    gameImage,
    gamePageGameImage,
    instaGameImage,
    animatedGameImage,
    descriptionSnippetId
  } = game[platform] ?? {};

  return {
    ...game,
    image: gameImage || '',
    pageImage: gamePageGameImage,
    snippetId: descriptionSnippetId,
    instaImage: instaGameImage,
    animatedImage: animatedGameImage,
    name: game.gameName,
    hasDemoSupport: !!game.supportDemo,
    provider: game.providerId,
    gameCode: game.gameId,
    group: getGameGroup(game.gameTypes ?? []),
    providerName: provider?.providerName || ''
  };
};

const normalizeGamesFromCms = ({
  games,
  providers,
  platformArg
}: {
  games: IGames;
  providers: GamesProvidersType;
  platformArg: PlatformType;
}): IGamesStore => {
  const isDesktop = platformArg === FORM_FACTORS_MAP.DESKTOP;
  return Object.values(games).reduce(
    (acc: IGamesStore, game) => {
      if (
        (isDesktop && game.availableOn.includes('desktop')) ||
        (!isDesktop && game.availableOn.includes('mobile'))
      ) {
        return {
          data: Object.assign(acc.data, {
            [game.gameId]: normalizeGameFromCms(
              game,
              providers[game.providerId],
              platformArg
            )
          }),
          ids: [...acc.ids, game.gameId],
          launchIdToGameId: Object.assign(acc.launchIdToGameId, {
            [game.launchGameId]: game.gameId
          })
        };
      }
      return acc;
    },
    { data: {}, ids: [], launchIdToGameId: {} }
  );
};

export const formatProvider = (provider: IGamesProvider) => ({
  ...provider,
  id: provider.providerId // TODO: just use providerId
});

export interface INormalizeProvidersFromCmsOutput {
  data: Record<string, IGamesProviderNormalised>;
  feedProvidersGames: Record<string, string[]>;
  feedProvidersIds: string[];
  isLoaded: boolean;
}

const normalizeProvidersFromCms = (
  feedProvidersContentIds: string[],
  content: FeedContentType,
  providers: GamesProvidersType,
  disabledProviders: string[],
  normalizedGames: IGamesStore
): INormalizeProvidersFromCmsOutput => {
  const formattedProviders = Object.keys(providers).reduce<
    Record<string, IGamesProviderNormalised>
  >((acc, key) => {
    acc[key] = formatProvider(providers[key]);

    return acc;
  }, {});

  const { feedProvidersIds, feedProvidersGames } =
    feedProvidersContentIds.reduce<{
      feedProvidersGames: Record<string, string[]>;
      feedProvidersIds: string[];
    }>(
      (acc, key) => {
        const item = content[key];
        const provider = formattedProviders[item.name] || {};

        if (
          disabledProviders.includes(provider.providerId) ||
          isEmpty(provider) ||
          item.games.length === 0
        ) {
          return acc;
        }

        const filteredGames = item.games.filter(
          (gameId) => normalizedGames.data[gameId]
        );

        if (filteredGames.length === 0) {
          return acc;
        }

        return {
          feedProvidersGames: Object.assign(acc.feedProvidersGames, {
            [provider.id]: item.games
          }),
          feedProvidersIds: [...acc.feedProvidersIds, provider.id]
        };
      },
      { feedProvidersGames: {}, feedProvidersIds: [] }
    );
  return {
    data: formattedProviders,
    feedProvidersGames,
    feedProvidersIds,
    isLoaded: true
  };
};

export const parseGamesListFromCms = (
  data: IGetGamesFeedResponse,
  disabledProviders: string[] = [],
  feedName: string,
  platformArg: PlatformType
) => {
  const findedGamesFeed = data?.feeds.find(
    (feed) => feed.feedName === feedName
  );

  const gamesFeed = findedGamesFeed || {
    ...(get(data, 'feeds[0]') || {}),
    categories: get(data, 'feeds[0].categories') || [],
    providers: get(data, 'feeds[0].providers') || [],
    content: get(data, 'feeds[0].content') || {}
  };
  const normalizedGames = normalizeGamesFromCms({
    games: data.games,
    providers: data.providers,
    platformArg
  });

  return {
    feedName: gamesFeed.feedName || feedName,
    categories: normalizeCategoriesFromCms(
      gamesFeed.categories,
      gamesFeed.content,
      normalizedGames
    ),
    providers: normalizeProvidersFromCms(
      gamesFeed.providers,
      gamesFeed.content,
      data.providers,
      disabledProviders,
      normalizedGames
    ),
    games: normalizedGames,
    isLoading: false
  };
};

export const parseFavorites = (gamesList?: { id: string }[]) =>
  (gamesList || []).map((i) => i.id);

export const mapServiceIdsToProviderIds = (
  providers: Record<string, IGamesProviderNormalised>
) => {
  const serviceIdToProviderId: Record<string, string> = {};

  for (const provider of Object.values(providers)) {
    if (provider.serviceId) {
      serviceIdToProviderId[provider.serviceId] = provider.providerId;
    }
  }

  return serviceIdToProviderId;
};

const normalizeLiveRtpSection = (section: Camelized<IColdHotListGames>[]) => {
  return section.reduce<{
    ids: number[];
    data: Record<string, { rtp: string; arrowUp: boolean }>;
  }>(
    (acc, game) => {
      const { gameId, rtp, arrowUp } = game;

      return {
        data: Object.assign(acc.data, {
          [gameId]: { rtp, arrowUp }
        }),
        ids: [...acc.ids, gameId]
      };
    },
    { ids: [], data: {} }
  );
};
export const normalizeLiveRtpGames = (
  games: Camelized<ColdHotListGamesDataType>
) => {
  const { mixed = [], hot = [], cold = [] } = games;

  const mixedSection = normalizeLiveRtpSection(mixed);
  const hotSection = normalizeLiveRtpSection(hot);
  const coldSection = normalizeLiveRtpSection(cold);

  return {
    hot: hotSection.ids,
    cold: coldSection.ids,
    mixed: mixedSection.ids,
    data: {
      ...mixedSection.data,
      ...hotSection.data,
      ...coldSection.data
    }
  };
};

export const fillGameLiveDataMaps = (
  launchIdToGameLiveData: LaunchIdToGameLiveDataType,
  innerGameIdToLaunchId: InnerGameIdToLaunchIdType,
  gmGames: Camelized<IGameDetails>[] = []
) => {
  const newLaunchIdToGameLiveDataState = gmGames.reduce<{
    launchIdToGameLiveData: LaunchIdToGameLiveDataType;
    innerGameIdToLaunchId: InnerGameIdToLaunchIdType;
  }>(
    (acc, curGame) => {
      const innerGameId = curGame.platform?.desktop?.providerGameId;
      return {
        launchIdToGameLiveData: {
          ...acc.launchIdToGameLiveData,
          [curGame.gameId]: {}
        },
        innerGameIdToLaunchId: {
          ...acc.innerGameIdToLaunchId,
          [innerGameId]: curGame.gameId
        }
      };
    },
    { launchIdToGameLiveData, innerGameIdToLaunchId }
  );
  return newLaunchIdToGameLiveDataState;
};
